"use client";

import type { ButtonProps } from "./Button";
import type { ILinkProps } from "./Link";
import type { EventName } from "@mw/shared/analyticsEventNames";
import type { FormattedCTA } from "@utilities/payload";

import { captureEvent } from "@/app/_analytics/analytics";

import { Button } from "./Button";
import { Link } from "./Link";
import { LinkTracked } from "./LinkTracked";
import { ModalVideoPlayer } from "./ModalVideoPlayer";

export type CTAButtonProps = FormattedCTA &
  Omit<ILinkProps, "appearance" | "ariaLabel" | "asButton" | "href" | "label" | "onClick" | "type"> & {
    appearance?: ButtonProps["variant"] | ILinkProps["appearance"] | null;
    ariaLabel?: string | null;
    href?: ILinkProps["href"];
    label?: ILinkProps["label"];
    eventName?: EventName;
    eventProperties?: Record<string, unknown>;
  };

export const CTAButton = ({
  appearance,
  ariaLabel,
  buttonVariants,
  children,
  className,
  description,
  disabled,
  eventName,
  eventProperties,
  href,
  icon,
  iconPosition,
  iconSource,
  img,
  label,
  rel,
  showExternalLoading,
  target,
  variant,
  ...props
}: CTAButtonProps) => {
  const resolvedLabel = label?.trim() || undefined;
  const resolvedButtonVariants = {
    ...buttonVariants,
    size: buttonVariants?.size ?? "lg",
    variant: buttonVariants?.variant ?? appearance,
  };

  switch (props.action) {
    case "button": {
      const content = resolvedLabel || children || props.button.label;

      return (
        <ModalVideoPlayer
          customTrigger={
            <Button
              aria-label={ariaLabel ?? props.button.ariaLabel}
              className={className}
              disabled={disabled}
              icon={icon}
              iconPosition={iconPosition}
              onClick={
                eventName ? () => captureEvent(eventName, eventProperties, { transport: "sendBeacon" }) : undefined
              }
              {...resolvedButtonVariants}
            >
              {content}
            </Button>
          }
          label={props.button.label}
          video={props.button.video}
        />
      );
    }

    case "link": {
      const { action: _action, link, ...linkPassthroughProps } = props;
      if (!link) {
        return null;
      }

      const content = resolvedLabel || children || link.label;
      const linkProps = {
        ...linkPassthroughProps,
        ariaLabel: ariaLabel ?? link.ariaLabel,
        asButton: true,
        buttonVariants: resolvedButtonVariants,
        className,
        description,
        disabled,
        href: href ?? link.href,
        icon,
        iconPosition,
        iconSource: iconSource ?? link.iconSource,
        img: img ?? link.img,
        label: resolvedLabel ?? link.label,
        rel: rel ?? link.rel,
        showExternalLoading,
        target: target ?? link.target,
        variant,
      };

      if (eventName) {
        return (
          <LinkTracked {...linkProps} eventName={eventName} eventProperties={eventProperties}>
            {content}
          </LinkTracked>
        );
      }

      return <Link {...linkProps}>{content}</Link>;
    }

    default: {
      const _: never = props;
      return null;
    }
  }
};
